import { db } from '../firebase';
import { 
  doc, 
  getDoc, 
  setDoc,
  runTransaction,
  Timestamp 
} from 'firebase/firestore';

const SYSTEM_SETTINGS_DOC = 'system_settings';

export const SystemSettingsService = {
  // Get system settings
  getSettings: async () => {
    const settingsRef = doc(db, SYSTEM_SETTINGS_DOC, 'settings');
    const settingsDoc = await getDoc(settingsRef);
    
    if (!settingsDoc.exists()) {
      // Initialize with default settings
      const defaultSettings = {
        // Queue Settings
        maxConcurrentTasks: 5,
        taskQueueEnabled: true,
        taskTimeoutMinutes: 30,
        maxQueueSize: 100,
        retryAttempts: 3,
        retryDelaySeconds: 60,
        
        // Template Settings
        maxTemplatesPerUser: 50,
        templateSizeLimit: 1024 * 1024, // 1MB
        allowedTemplateTypes: ['product', 'collection', 'custom'],
        
        // Workflow Settings
        maxWorkflowsPerUser: 20,
        maxWorkflowSteps: 10,
        maxConcurrentWorkflows: 3,
        workflowTimeoutMinutes: 60,
        
        // Shopify Push Settings
        maxPushRetries: 5,
        pushBatchSize: 50,
        pushRateLimit: 2, // requests per second
        pushTimeoutMinutes: 120,
        
        // System Maintenance
        maintenanceMode: false,
        maintenanceMessage: '',
        lastMaintenanceDate: null,
        scheduledMaintenanceDate: null,
        scheduledMaintenanceEndDate: null,
        maintenanceTitle: 'System Maintenance',
        
        // Registration Settings
        disableRegularSignUp: false, // When true, only affiliate links can be used for registration
        
        // Timestamps
        lastUpdated: Timestamp.now(),
        updatedBy: 'system'
      };
      
      await setDoc(settingsRef, defaultSettings);
      return defaultSettings;
    }
    
    return settingsDoc.data();
  },

  // Update system settings
  updateSettings: async (newSettings, adminId) => {
    const settingsRef = doc(db, SYSTEM_SETTINGS_DOC, 'settings');
    
    await runTransaction(db, async (transaction) => {
      const settingsDoc = await transaction.get(settingsRef);
      
      const currentSettings = settingsDoc.exists() ? settingsDoc.data() : {};
      const updatedSettings = {
        ...currentSettings,
        ...newSettings,
        lastUpdated: Timestamp.now(),
        updatedBy: adminId
      };
      
      transaction.set(settingsRef, updatedSettings);
    });
  },

  // Check if system is in maintenance mode
  isInMaintenanceMode: async () => {
    const settings = await SystemSettingsService.getSettings();
    return settings.maintenanceMode === true;
  },

  // Get maintenance information
  getMaintenanceInfo: async () => {
    const settings = await SystemSettingsService.getSettings();
    console.log('Raw settings from Firestore:', settings);
    
    // Check if scheduledMaintenanceDate exists and is a Timestamp
    if (settings.scheduledMaintenanceDate) {
      console.log('Scheduled maintenance date type:', typeof settings.scheduledMaintenanceDate);
      console.log('Is Timestamp:', settings.scheduledMaintenanceDate instanceof Timestamp);
    }
    
    // Ensure we return the raw Firestore data without any conversion
    // This ensures consistent behavior across different user sessions
    return {
      maintenanceMode: settings.maintenanceMode || false,
      maintenanceMessage: settings.maintenanceMessage || '',
      maintenanceTitle: settings.maintenanceTitle || 'System Maintenance',
      scheduledMaintenanceDate: settings.scheduledMaintenanceDate || null,
      scheduledMaintenanceEndDate: settings.scheduledMaintenanceEndDate || null,
      lastMaintenanceDate: settings.lastMaintenanceDate || null
    };
  },

  // Schedule maintenance
  scheduleMaintenance: async (startDate, endDate, title, message, adminId) => {
    const settingsRef = doc(db, SYSTEM_SETTINGS_DOC, 'settings');
    
    await runTransaction(db, async (transaction) => {
      const settingsDoc = await transaction.get(settingsRef);
      const currentSettings = settingsDoc.exists() ? settingsDoc.data() : {};
      
      const updatedSettings = {
        ...currentSettings,
        maintenanceMode: false,
        maintenanceMessage: message,
        maintenanceTitle: title || 'System Maintenance',
        scheduledMaintenanceDate: startDate instanceof Date ? Timestamp.fromDate(startDate) : startDate,
        scheduledMaintenanceEndDate: endDate instanceof Date ? Timestamp.fromDate(endDate) : endDate,
        lastUpdated: Timestamp.now(),
        updatedBy: adminId
      };
      
      transaction.set(settingsRef, updatedSettings);
    });
  },

  // Start maintenance mode
  startMaintenance: async (adminId) => {
    const settingsRef = doc(db, SYSTEM_SETTINGS_DOC, 'settings');
    
    await runTransaction(db, async (transaction) => {
      const settingsDoc = await transaction.get(settingsRef);
      const currentSettings = settingsDoc.exists() ? settingsDoc.data() : {};
      
      const updatedSettings = {
        ...currentSettings,
        maintenanceMode: true,
        lastMaintenanceDate: Timestamp.now(),
        lastUpdated: Timestamp.now(),
        updatedBy: adminId
      };
      
      transaction.set(settingsRef, updatedSettings);
    });
  },

  // End maintenance mode
  endMaintenance: async (adminId) => {
    const settingsRef = doc(db, SYSTEM_SETTINGS_DOC, 'settings');
    
    await runTransaction(db, async (transaction) => {
      const settingsDoc = await transaction.get(settingsRef);
      const currentSettings = settingsDoc.exists() ? settingsDoc.data() : {};
      
      const updatedSettings = {
        ...currentSettings,
        maintenanceMode: false,
        maintenanceMessage: '',
        scheduledMaintenanceDate: null,
        scheduledMaintenanceEndDate: null,
        lastUpdated: Timestamp.now(),
        updatedBy: adminId
      };
      
      transaction.set(settingsRef, updatedSettings);
    });
  },

  // Cancel scheduled maintenance
  cancelScheduledMaintenance: async (adminId) => {
    const settingsRef = doc(db, SYSTEM_SETTINGS_DOC, 'settings');
    
    await runTransaction(db, async (transaction) => {
      const settingsDoc = await transaction.get(settingsRef);
      const currentSettings = settingsDoc.exists() ? settingsDoc.data() : {};
      
      const updatedSettings = {
        ...currentSettings,
        scheduledMaintenanceDate: null,
        scheduledMaintenanceEndDate: null,
        lastUpdated: Timestamp.now(),
        updatedBy: adminId
      };
      
      transaction.set(settingsRef, updatedSettings);
    });
  },

  // Check if regular sign-ups are disabled (only affiliate links allowed)
  isRegularSignUpDisabled: async () => {
    const settings = await SystemSettingsService.getSettings();
    return settings.disableRegularSignUp === true;
  },

  // Public method to check sign-up restrictions without authentication
  // This is used during the registration process before a user is authenticated
  getPublicSignUpRestrictions: async () => {
    try {
      const settingsRef = doc(db, SYSTEM_SETTINGS_DOC, 'settings');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        return {
          disableRegularSignUp: settingsDoc.data().disableRegularSignUp === true
        };
      }
      
      return { disableRegularSignUp: false };
    } catch (error) {
      console.error('Error getting public sign-up restrictions:', error);
      return { disableRegularSignUp: false }; // Default to allowing sign-ups if there's an error
    }
  },

  // Toggle regular sign-up setting
  toggleRegularSignUp: async (disabled, adminId) => {
    const settingsRef = doc(db, SYSTEM_SETTINGS_DOC, 'settings');
    
    await runTransaction(db, async (transaction) => {
      const settingsDoc = await transaction.get(settingsRef);
      const currentSettings = settingsDoc.exists() ? settingsDoc.data() : {};
      
      const updatedSettings = {
        ...currentSettings,
        disableRegularSignUp: disabled,
        lastUpdated: Timestamp.now(),
        updatedBy: adminId
      };
      
      transaction.set(settingsRef, updatedSettings);
    });
  }
};
